import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, PlayCircle, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { AppText, Card, Badge } from "./ui";

const STABILITY_MAX = 5;

export function ExerciseRow({ ex, compact }) {
  const [video, setVideo] = useState(false);
  return (
    <div className="print-ex flex gap-3 items-start" data-testid={`gainage-ex-${slug(ex.name)}`}>
      {ex.image ? (
        <div className={`shrink-0 rounded-xl overflow-hidden bg-km-tertiary ${compact ? "w-16 h-16" : "w-24 h-24"}`}>
          {video && ex.video ? <video src={ex.video} controls autoPlay playsInline className="w-full h-full object-cover" /> : <img src={ex.image} alt={ex.name} loading="lazy" className="w-full h-full object-cover" />}
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <AppText variant="bodyStrong">{ex.name}</AppText>
          <Badge text={ex.dosage} tone="brand" />
        </div>
        {ex.focus ? <AppText variant="caption" className="text-km-muted">{ex.focus}</AppText> : null}
        <AppText variant={compact ? "caption" : "body"} className="text-km-ontertiary mt-0.5">{ex.description}</AppText>
        {ex.video && !video ? (
          <button onClick={() => setVideo(true)} className="no-print mt-1 flex items-center gap-1 text-km-coral font-text font-bold text-[14px] min-h-[32px]" data-testid={`gainage-video-${slug(ex.name)}`}>
            <PlayCircle size={16} /> Voir la démo vidéo
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function VideoLibrary({ lib, compact }) {
  const [playing, setPlaying] = useState(null);
  if (!lib) return null;
  return (
    <Card className="flex flex-col gap-3 no-print" testID="gainage-library">
      <div>
        <AppText variant="label" className="text-km-coral">{lib.title}</AppText>
        <AppText variant="caption" className="text-km-ontertiary mt-1">{lib.intro}</AppText>
      </div>
      <div className={`grid gap-3 ${compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"}`}>
        {lib.items.map((it) => (
          <button key={it.num} type="button" onClick={() => setPlaying(playing === it.num ? null : it.num)} data-testid={`gainage-lib-${it.num}`} className="text-left flex flex-col gap-1">
            <div className="relative w-full aspect-[3/4] rounded-xl overflow-hidden bg-km-tertiary">
              {playing === it.num
                ? <video src={it.video} muted loop autoPlay playsInline preload="none" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><PlayCircle size={36} className="text-km-coral" /></div>}
              <span className="absolute top-1.5 left-1.5 px-1.5 rounded-md bg-white/85 text-[11px] font-bold">{it.num}</span>
            </div>
            <AppText variant="caption" className="font-bold leading-4">{it.name}</AppText>
            {it.sub ? <AppText variant="caption" className="text-km-muted leading-4 -mt-1">{it.sub}</AppText> : null}
          </button>
        ))}
      </div>
      <AppText variant="caption" className="text-km-muted">Dosage : {lib.dosage}.</AppText>
      <div className="flex flex-wrap gap-4">
        <a href={lib.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-km-coral font-bold min-h-[40px]" data-testid="gainage-library-link"><ExternalLink size={16} /> La page complète sur topperf.com</a>
        {lib.tool ? <a href={lib.tool.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-km-ink font-bold min-h-[40px]" data-testid="gainage-library-tool"><ExternalLink size={16} /> {lib.tool.label}</a> : null}
      </div>
    </Card>
  );
}

export function FamilyBlock({ family, compact }) {
  return (
    <div className="print-family flex flex-col gap-3" data-testid={`gainage-family-${family.key}`}>
      <div>
        <AppText variant="label" className="text-km-coral">{family.num} · {family.label}</AppText>
        <AppText variant="caption" className="text-km-muted">{family.dosage}</AppText>
      </div>
      {family.exercises.map((ex) => <ExerciseRow key={ex.name} ex={ex} compact={compact} />)}
    </div>
  );
}

export function StabilityBlock({ program }) {
  const [tab, setTab] = useState(0);
  if (!program) return null;
  const s = program.sessions[tab];
  return (
    <Card className="flex flex-col gap-3 mt-4 border-2 border-km-sage/50" testID="stability-block">
      <div>
        <AppText variant="label" className="text-km-sage">Ton bloc stabilité</AppText>
        <AppText variant="subtitle">{program.title}</AppText>
        <AppText variant="caption" className="text-km-muted mt-1">{program.for} Trois séances par semaine, en plus du gainage vertical quotidien.</AppText>
      </div>
      <div className="flex flex-wrap gap-2">
        {program.sessions.map((x, i) => (
          <button key={x.key} onClick={() => setTab(i)} data-testid={`stability-tab-${x.key}`}
            className={`px-3 min-h-[40px] rounded-full border-2 font-text font-bold text-[14px] ${i === tab ? "border-km-sage bg-km-sage/20 text-km-ink" : "border-km-border bg-white text-km-ink"}`}>{x.day} · {x.title}</button>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {s.exercises.map((ex, i) => (
          <div key={ex.name} className="flex flex-col gap-0.5 border-t border-km-divider pt-2" data-testid={`stability-ex-${i}`}>
            <AppText variant="bodyStrong">{i + 1} · {ex.name}</AppText>
            {ex.dosage.map((d) => <AppText key={d} variant="caption" className="text-km-coral font-bold">{d}</AppText>)}
            <AppText variant="caption" className="text-km-ontertiary">{ex.description}</AppText>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function GainageSection({ stabilityScore, motor, bare }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stab, setStab] = useState(null);
  const [open, setOpen] = useState(false);
  const needsStability = typeof stabilityScore === "number" && stabilityScore <= STABILITY_MAX;
  useEffect(() => {
    api.gainage().then(setData).catch(() => {});
    if (needsStability) api.gainageStabilite().then(setStab).catch(() => {});
  }, [needsStability]);
  if (!data) return null;
  const core = data.families[0];
  return (
    <div className={bare ? "" : "mt-8"} data-testid="gainage-section">
      {bare ? null : <><div className="w-10 h-1.5 rounded-full bg-km-coral mb-2" />
      <AppText variant="title" className="mb-1">Ton gainage vertical</AppText>
      <AppText variant="caption" className="text-km-muted mb-4">{data.daily_minutes} tous les jours, idéalement avant la sortie. Debout, jamais au sol.</AppText></>}
      <Card className="flex flex-col gap-3">
        <AppText variant="caption" className="text-km-ontertiary">{data.test}</AppText>
        <div className="flex flex-col gap-3">
          {(open ? core.exercises : core.exercises.slice(0, 3)).map((ex) => <ExerciseRow key={ex.name} ex={ex} compact />)}
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          <button onClick={() => setOpen((v) => !v)} className="text-km-coral font-text font-bold min-h-[40px]" data-testid="gainage-toggle">{open ? "Réduire" : `Voir les ${core.exercises.length} exercices`}</button>
          <button onClick={() => navigate("/gainage")} className="flex items-center gap-1.5 text-km-ink font-text font-bold min-h-[40px]" data-testid="gainage-sheet-link"><Printer size={16} /> Fiche complète à imprimer</button>
          {data.library ? <a href={data.library.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-km-ink font-text font-bold min-h-[40px]" data-testid="gainage-vary-link"><ExternalLink size={16} /> Varier : 14 exercices en vidéo (topperf)</a> : null}
        </div>
      </Card>
      {needsStability ? <StabilityBlock program={stab} /> : null}
      {needsStability && motor && motor !== "terrien" ? <AppText variant="caption" className="text-km-muted mt-2">Ce bloc a été conçu pour les profils terriens : garde le gainage vertical comme priorité, et parles-en à Éric si un exercice te gêne.</AppText> : null}
    </div>
  );
}

function slug(s) { return String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""); }
